import { Link, useParams } from "react-router-dom";
import { useStore } from "@/hooks/useStore";
import { useAuth } from "@/hooks/useAuth";
import { Panel, PageTitle } from "@/components/ui/Panel";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import type { Card } from "@/types/card";

export function CardDetail() {
  const { id } = useParams();
  const { state } = useStore();
  const { profile } = useAuth();
  const card = state.cards.find((c) => c.id === id);

  if (!card) {
    return (
      <Panel>
        <p className="text-sm text-[#64748b]">Card not found.</p>
        <Link className="gm-link" to="/cards">Back to all cards</Link>
      </Panel>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Link to="/cards" className="text-xs text-[#64748b] hover:text-amber-soft">← All Cards</Link>
        {profile?.role === "admin" && (
          <Link to={`/cards/${card.id}/edit`}>
            <Button variant="secondary">Edit</Button>
          </Link>
        )}
      </div>
      <div className="grid gap-4 lg:grid-cols-[2fr_3fr]">
        <Panel className="p-0">
          <div className="aspect-[3/4] overflow-hidden rounded-lg bg-[#0f1115]">
            {card.imageDataUrl ? (
              <img src={card.imageDataUrl} alt={card.name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-[#64748b] text-xs">no image</div>
            )}
          </div>
        </Panel>
        <div className="space-y-4">
          <div>
            <PageTitle>{card.name}</PageTitle>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[#94a3b8]">
              <Badge>{card.cardType}</Badge>
              <Badge tone="amber">{card.rarity}</Badge>
              <Badge tone={card.status === "Finalized" ? "green" : card.status === "Testing" ? "amber" : "default"}>
                {card.status}
              </Badge>
              {card.setCardNumber && <span className="font-mono">{card.setCardNumber}</span>}
              <span>· {card.set}</span>
            </div>
          </div>

          <CardStats card={card} />

          {card.rulesText && (
            <Panel>
              <h3 className="gm-label">Rules text</h3>
              <p className="whitespace-pre-wrap text-sm">{card.rulesText}</p>
            </Panel>
          )}

          <Notes card={card} />
        </div>
      </div>
    </div>
  );
}

function CardStats({ card }: { card: Card }) {
  const rows: { label: string; value: string }[] = [];
  const push = (label: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== "") {
      rows.push({ label, value: Array.isArray(value) ? value.join(", ") : String(value) });
    }
  };

  if (card.championFields) {
    const f = card.championFields;
    push("Speed", f.speed);
    push("Faction", f.faction);
    push("Champion type", f.championType);
    push("Equipment stat", f.equipmentStat);
    push("Ability", f.ability);
  }
  if (card.armorFields) {
    push("Weight", card.armorFields.weight);
    push("Block value", card.armorFields.blockValue);
    push("Ability", card.armorFields.ability);
  }
  if (card.equipmentFields) {
    const f = card.equipmentFields;
    push("Subtype", f.equipmentSubtype);
    push("Secondary subtype", f.secondarySubtype);
    push("Attack type / weight", `${f.attackType ?? ""} / ${f.attackWeight ?? ""}`);
    push("Defend type / weight", `${f.defendType ?? ""} / ${f.defendWeight ?? ""}`);
    push("Power", f.powerValue);
    push("Block", f.blockValue);
    push("Stamina", f.staminaCost);
    push("Equipment value", f.equipmentValue);
    push("Ability", f.ability);
  }
  if (card.signatureMoveFields) {
    const f = card.signatureMoveFields;
    push("Condition", f.condition);
    push("Attack type / weight", `${f.attackType ?? ""} / ${f.attackWeight ?? ""}`);
    push("Power", f.powerValue);
    push("Block", f.blockValue);
    push("Stamina", f.staminaCost);
    push("Ability", f.ability);
  }
  if (card.stanceFields) {
    push("Type requirement", card.stanceFields.typeRequirement);
    push("Ability", card.stanceFields.ability);
  }
  if (card.utilityFields) {
    push("Utility type", card.utilityFields.utilityType);
    push("Type requirement", card.utilityFields.typeRequirement);
    push("Ability", card.utilityFields.ability);
  }
  if (card.attackDefendFields) {
    const f = card.attackDefendFields;
    push("Weight", f.weight);
    push("Type requirement", f.typeRequirement);
    push("Special move", f.isSpecialMove ? "yes" : "no");
    push("Attack type", f.attackType);
    push("Defend type", f.defendType);
    push("Power", f.powerValue);
    push("Block", f.blockValue);
    push("Stamina", f.staminaCost);
    push("Attack ability", f.attackAbility);
    push("Defend ability", f.defendAbility);
    push("Counter req.", f.counterRequirement);
    push("Counter ability", f.counterAbility);
  }
  if (card.tokenFields) {
    push("Ability", card.tokenFields.ability);
    push("Stamina", card.tokenFields.staminaCost);
  }
  if (card.attackKeywords?.length) push("Attack keywords", card.attackKeywords);
  if (card.defendKeywords?.length) push("Defend keywords", card.defendKeywords);
  if (card.keywords?.length) push("Keywords", card.keywords);
  if (card.mechanics?.length) push("Mechanic tags", card.mechanics);

  return (
    <Panel>
      <h3 className="gm-label">Stats & abilities</h3>
      <dl className="grid gap-2 text-sm sm:grid-cols-2">
        {rows.map((r) => (
          <div key={r.label} className="border-b border-[#22262f] pb-1.5">
            <dt className="text-[10px] uppercase tracking-wider text-[#64748b]">{r.label}</dt>
            <dd className="whitespace-pre-wrap text-sm">{r.value}</dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
}

function Notes({ card }: { card: Card }) {
  const items: { title: string; v?: string }[] = [
    { title: "Designer notes", v: card.designerNotes },
    { title: "Playtest notes", v: card.playtestNotes },
    { title: "Balance concerns", v: card.balanceConcerns },
  ].filter((x) => x.v && x.v.trim());
  if (items.length === 0) return null;
  return (
    <div className="space-y-3">
      {items.map((it) => (
        <Panel key={it.title}>
          <h3 className="gm-label">{it.title}</h3>
          <p className="whitespace-pre-wrap text-sm text-[#cbd5e1]">{it.v}</p>
        </Panel>
      ))}
    </div>
  );
}
